import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';

const ASSISTANT_URL = 'https://fieldworker-assistant-672166797950.us-east1.run.app/';

const FAB_SIZE = 56;
const EDGE_MARGIN = 16;
const TOP_MARGIN = 70;      // stay clear of the navbar
const BOTTOM_MARGIN = 24;
const DRAG_THRESHOLD = 4;   // px of movement before a click counts as a drag
const POSITION_KEY = 'fw_assistant_fab_position';

// the button rests against the left or right edge; only the side and the vertical
// offset are persisted, so a saved spot still makes sense on a differently sized screen
const clampY = (y) => Math.min(
  Math.max(y, TOP_MARGIN),
  Math.max(TOP_MARGIN, window.innerHeight - FAB_SIZE - BOTTOM_MARGIN)
);

const xForSide = (side) => (side === 'left'
  ? EDGE_MARGIN
  : Math.max(EDGE_MARGIN, window.innerWidth - FAB_SIZE - EDGE_MARGIN));

const sideForX = (x) => (x + FAB_SIZE / 2 < window.innerWidth / 2 ? 'left' : 'right');

const defaultAnchor = () => ({ side: 'right', y: window.innerHeight - FAB_SIZE - BOTTOM_MARGIN });

const readStoredAnchor = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(POSITION_KEY));
    if (stored && (stored.side === 'left' || stored.side === 'right') && Number.isFinite(stored.y)) {
      return stored;
    }
  } catch (err) {
    // malformed or unavailable storage — fall back to the default corner
  }
  return null;
};

export default function FieldWorkerAssistant({ open: openProp, onToggle }) {
  const open = openProp ?? false;
  const setOpen = onToggle ?? (() => {});

  const closeChat = useCallback(() => setOpen(false), [setOpen]);

  const [anchor, setAnchor] = useState(() => readStoredAnchor() || defaultAnchor());
  const [dragPos, setDragPos] = useState(null); // live position while dragging
  const dragOriginRef = useRef(null);
  const movedRef = useRef(false);

  // keep the button on screen when the window is resized
  useEffect(() => {
    const handleResize = () => setAnchor((prev) => ({ ...prev, y: clampY(prev.y) }));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const restingPos = { x: xForSide(anchor.side), y: clampY(anchor.y) };
  const { x, y } = dragPos || restingPos;

  const handlePointerDown = (event) => {
    if (event.button !== 0) return;
    dragOriginRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      originX: restingPos.x,
      originY: restingPos.y,
    };
    movedRef.current = false;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const origin = dragOriginRef.current;
    if (!origin) return;

    const dx = event.clientX - origin.pointerX;
    const dy = event.clientY - origin.pointerY;
    if (!movedRef.current && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;

    movedRef.current = true;
    setDragPos({
      x: Math.min(
        Math.max(origin.originX + dx, EDGE_MARGIN),
        Math.max(EDGE_MARGIN, window.innerWidth - FAB_SIZE - EDGE_MARGIN)
      ),
      y: clampY(origin.originY + dy),
    });
  };

  const handlePointerUp = (event) => {
    const wasDragging = !!dragOriginRef.current;
    dragOriginRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);

    if (!wasDragging || !movedRef.current || !dragPos) {
      setDragPos(null);
      return;
    }

    // snap to the nearest edge and remember it
    const next = { side: sideForX(dragPos.x), y: dragPos.y };
    setAnchor(next);
    setDragPos(null);
    try {
      localStorage.setItem(POSITION_KEY, JSON.stringify(next));
    } catch (err) {
      // storage unavailable — the position just won't survive a reload
    }
  };

  const handleClick = () => {
    if (movedRef.current) {
      movedRef.current = false; // that pointer sequence was a drag, not a click
      return;
    }
    setOpen(true);
  };

  const widget = (
    <>
      {open && (
        <div style={styles.window}>
          <div style={styles.header}>
            <div style={styles.headerTitle}>
              <SmartToyIcon fontSize="small" />
              FieldWorker Assistant
            </div>
            <div style={styles.headerActions}>
              <Tooltip title="Minimize" placement="top">
                <IconButton size="small" onClick={closeChat} style={{ color: '#fff' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          <iframe
            src={ASSISTANT_URL}
            title="FieldWorker Assistant"
            style={styles.iframe}
            allow="clipboard-read; clipboard-write; microphone"
          />
        </div>
      )}

      {!open && (
        <Tooltip
          title="Chat with FieldWorker Assistant — drag to move"
          placement={anchor.side === 'left' ? 'right' : 'left'}
          disableHoverListener={!!dragPos}
        >
          <IconButton
            onClick={handleClick}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{
              ...styles.fab,
              left: `${x}px`,
              top: `${y}px`,
              cursor: dragPos ? 'grabbing' : 'grab',
            }}
          >
            <SmartToyIcon />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  return ReactDOM.createPortal(widget, document.body);
}

const styles = {
  fab: {
    position: 'fixed',
    // left/top are supplied by the drag logic; the default resting spot is the
    // bottom-right corner, same as before
    zIndex: 1300,
    backgroundColor: '#1976d2',
    color: '#fff',
    width: `${FAB_SIZE}px`,
    height: `${FAB_SIZE}px`,
    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
    touchAction: 'none', // let a touch drag move the button instead of scrolling the page
  },
  window: {
    position: 'fixed',
    top: '70px',
    bottom: 0,
    right: 0,
    zIndex: 1200,
    width: '420px',
    borderRadius: '12px 0 0 0',
    boxShadow: '-4px 0 16px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: '#fff',
    fontFamily: 'Roboto, sans-serif',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    backgroundColor: '#1976d2',
    color: '#fff',
    flexShrink: 0,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 600,
    fontSize: '15px',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
  },
  iframe: {
    flex: 1,
    width: '100%',
    border: 'none',
    backgroundColor: '#fff',
  },
};
